import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";
import {
  Button,
  Typography,
  Radio,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import pdfDownloadHelperBase from "@/features/campaigns/shared/helpers/pdfDownloadHelper";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { reportUserActivity } from "@/shared/helpers/userActivityHelper";
import { downloadCSVFile } from "@/helpers/utils";
import {
  formatDate,
  isBrandHasDrillFeature,
  parseDateString,
} from "@/features/campaigns/shared/helpers/drillUpDownHelper";

const options = ["CSV", "PDF"];

type DateRangePart = {
  month: number | string;
  year: number | string;
};

type StandardRange = {
  from: DateRangePart;
  to: DateRangePart;
};

type DrillRange = [string, string];

type DownloadValues = {
  channel_id?: string | number;
  customRange?: DrillRange | StandardRange;
  filter?: string;
  group_id?: string | number;
  location_id?: string | number;
};

type DownloadCampaign = {
  id?: number | string;
};

type SelectDownloadReportsProps = {
  campaign?: DownloadCampaign | null;
  elId?: string;
  fileName?: string;
  initialRange?: StandardRange | null;
  values: DownloadValues;
};

type ReportRequestData = {
  campaign_id: number | string;
  channel_id: string | number;
  downloadReport: boolean;
  end_period: string | false | undefined;
  filter?: string;
  fk_brand_id?: number | string;
  fk_participant_id: number | string;
  fk_role_id?: number | string;
  group_id: string | number;
  location_id: string | number;
  participant_system_id?: string;
  start_period: string | false | undefined;
  system_generated_brand_id?: string | number;
  system_generated_campaign_id: string;
  userId?: number | string;
};

type SelectedBrand = {
  id?: number | string;
  system_generated_brand_id?: number | string;
};

type LoggedInUser = {
  fk_role_id?: number | string;
  id?: number | string;
  participantData?: {
    id?: number | string;
    system_generated_participant_id?: string;
  };
};

type DownloadContext = {
  loggedInUser?: LoggedInUser;
  selectedBrand?: SelectedBrand;
};

type CsvResponse = {
  data?: string | null;
  headers?: {
    "content-disposition"?: string;
  } | null;
};

const isStandardRange = (
  range: DownloadValues["customRange"]
): range is StandardRange =>
  Boolean(range && !Array.isArray(range) && "from" in range && "to" in range);

const pdfDownloadHelper = pdfDownloadHelperBase as unknown as (
  _processing: boolean,
  _setProcessing: React.Dispatch<React.SetStateAction<boolean>>,
  _elId?: string,
  _fileName?: string
) => void;

export default function SelectDownloadReports({
  fileName,
  campaign,
  values,
  elId,
  initialRange,
}: SelectDownloadReportsProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const { selectedBrand, loggedInUser } = useContext(
    GlobalContext
  ) as DownloadContext;
  const loader = useContext(LoaderContext);
  const { campaignSysId } = useParams();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loader(processing);
  }, [processing, loader]);

  const brandSystemId = selectedBrand?.system_generated_brand_id
    ? String(selectedBrand.system_generated_brand_id)
    : undefined;

  const startPeriod = useMemo(() => {
    if (isBrandHasDrillFeature(brandSystemId)) {
      if (values.filter === "custom" && values.customRange) {
        return formatDate(parseDateString(values.customRange[0]));
      }
      if (initialRange) {
        return `${initialRange.from.year}-${String(
          initialRange.from.month
        ).padStart(2, "0")}`;
      }
      return "";
    }
    return values.filter === "custom" && values.customRange
      ? isStandardRange(values.customRange)
        ? `${values.customRange.from.year}-${values.customRange.from.month}`
        : ""
      : initialRange && `${initialRange.from.year}-${initialRange.from.month}`;
  }, [values.filter, values.customRange, initialRange, brandSystemId]);

  const endPeriod = useMemo(() => {
    if (isBrandHasDrillFeature(brandSystemId)) {
      if (values.filter === "custom" && values.customRange) {
        return formatDate(parseDateString(values.customRange[1]));
      }
      if (initialRange) {
        return `${initialRange.to.year}-${String(
          initialRange.to.month
        ).padStart(2, "0")}`;
      }
      return "";
    }
    return values.filter === "custom" && values.customRange
      ? isStandardRange(values.customRange)
        ? `${values.customRange.to.year}-${values.customRange.to.month}`
        : ""
      : initialRange && `${initialRange.to.year}-${initialRange.to.month}`;
  }, [values.filter, values.customRange, initialRange, brandSystemId]);

  const requestData = useMemo<ReportRequestData | null>(() => {
    if (!campaign || !campaign.id) return null;

    return {
      campaign_id: campaign.id,
      system_generated_campaign_id: `${campaignSysId}`,
      system_generated_brand_id: selectedBrand?.system_generated_brand_id,
      fk_brand_id: selectedBrand?.id,
      location_id: values.location_id || "",
      group_id: values.group_id || "",
      channel_id: values.channel_id || "",
      userId: loggedInUser?.id,
      fk_participant_id: loggedInUser?.participantData?.id || 0,
      downloadReport: true,
      filter: values.filter,
      participant_system_id:
        loggedInUser?.participantData?.system_generated_participant_id,
      fk_role_id: loggedInUser?.fk_role_id,
      start_period: startPeriod,
      end_period: endPeriod,
    };
  }, [
    campaign,
    selectedBrand,
    loggedInUser,
    campaignSysId,
    values,
    startPeriod,
    endPeriod,
  ]);

  const handleClick = async () => {
    if (selectedIndex === 1) {
      pdfDownloadHelper(processing, setProcessing, elId, fileName);
    } else {
      try {
        setProcessing(true);
        reportUserActivity("download_csv");
        toastr.info("CSV Download", "CSV download started");
        const res = (await campaignService.getCampaignReportCsv(
          requestData
        )) as CsvResponse;

        // Check if headers exist or if res.data exists - consider both as success
        if (res && (res.headers || res.data)) {
          toastr.success("Success", "CSV Report downloaded successfully");
          if (res.data) {
            const { "content-disposition": contentDisposition } =
              res && res.headers ? res.headers : null;

            const fileUnlinkPath = res.data ? res.data : null;
            const fileName =
              contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
              "campaign-report.csv";
            downloadCSVFile(fileUnlinkPath, fileName, "text/csv");
            setProcessing(false);
          } else {
            setProcessing(false);
          }
        } else {
          setProcessing(false);
        }
      } catch (error) {
        setProcessing(false);
      }
    }
  };

  const handleMenuItemClick = (_: React.MouseEvent, index: number) => {
    setSelectedIndex(index);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      event.target instanceof Node &&
      anchorRef.current.contains(event.target)
    )
      return;
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        variant="contained"
        ref={anchorRef}
        aria-label="split button"
        sx={{ width: "100%" }}
      >
        <Button
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            height: 36,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          Download {options[selectedIndex]}
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        sx={{
          zIndex: 999,
          fontSize: "0.8rem",
          width: "90%",
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList sx={{ width: "100%" }}>
                  <Typography sx={{ fontSize: "0.75rem", ml: 1.25, mb: 0.5 }}>
                    Choose a format:
                  </Typography>

                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        "&.Mui-selected": {
                          backgroundColor: "var(--primary-tint)",
                        },
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Radio
                          checked={index === selectedIndex}
                          sx={{
                            p: "4px",
                            "& .MuiSvgIcon-root": {
                              width: 14,
                              height: 14,
                              "& path": {
                                fill: "primary.main",
                              },
                            },
                          }}
                        />
                        <Typography sx={{ fontSize: "0.75rem" }}>
                          {option}
                        </Typography>
                      </div>
                      {index === selectedIndex && (
                        <CheckIcon
                          sx={{
                            color: "primary.main",
                            ml: 1,
                            fontSize: "medium",
                          }}
                        />
                      )}
                    </MenuItem>
                  ))}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setOpen(false);
                        setTimeout(() => handleClick(), 0);
                      }}
                    >
                      Download
                    </Button>
                  </div>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
